import { Component, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocationProduct } from '../../../charging-station-location-model';
import { AsyncPipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormField, MatOption, MatSelect, MatLabel, MatHint, MatSuffix } from '@angular/material/select';
import { InputMaskModule } from '@ngneat/input-mask';
import { EnumToArrayPipe } from '../../../../../shared/pipes/enum-to-array.pipe';
import { TranslateLocationProductPipe } from '../../../../../shared/pipes/translate-location-product-pipe';
import { ChargingStationLocationCreatedEditDialogService } from '../../charging-station-location-created-edit-dialog.service';
import { combineLatest, distinctUntilChanged, filter, map, of, Subject, switchMap, tap } from 'rxjs';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import OrganizationService from 'src/app/shared/services/organization.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { MatIcon } from '@angular/material/icon';
import { AdmG4AgreementNumberComponent } from '../../../../../shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import { DkCurrencyPipe } from '../../../../../shared/pipes/dk-currency';

export interface Step4Form {
  product: FormControl<LocationProduct | null>;
  agreementNumber: FormControl<number | null>;
  payoutKickback: FormControl<boolean>;
  kickbackId: FormControl<number | null>;
  hasPowerPriceExclusion: FormControl<boolean>;
}

@Component({
  selector: 'adm-csl-step4',
  imports: [
    MatFormField,
    MatInputModule,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    InputMaskModule,
    MatLabel,
    AsyncPipe,
    EnumToArrayPipe,
    TranslateLocationProductPipe,
    DkCurrencyPipe,
    MatSlideToggle,
    MatTooltip,
    MatHint,
    MatSuffix,
    MatIcon,
    CustomDropDownArrowDirective,
    AdmG4AgreementNumberComponent,
  ],
  templateUrl: './step4.component.html',
  styleUrl: './step4.component.scss',
})
export class ChargingStationLocationEditDialogStep4Component implements OnInit {
  readonly form = input.required<FormGroup<Step4Form>>();
  readonly hasOkPowerMeter = input.required<boolean>();

  readonly organizationService = inject(OrganizationService);
  readonly service = inject(ChargingStationLocationCreatedEditDialogService);
  private readonly destroyRef = inject(DestroyRef);

  readonly products = LocationProduct;
  private readonly reloadKickbacks$ = new Subject<void>();
  readonly payoutKickbackEnabled = signal(false);

  ngOnInit(): void {
    this.service.organizationChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(organizationId => {
      void organizationId;
    });

    this.form()
      .controls.payoutKickback.valueChanges.pipe(
        tap(enabled => {
          this.payoutKickbackEnabled.set(enabled);
          if (enabled) {
            this.reloadKickbacks$.next();
          } else {
            this.form().patchValue({ kickbackId: null });
          }
          this.form().controls.kickbackId.setValidators(enabled ? [Validators.required] : []);
          this.form().controls.kickbackId.updateValueAndValidity({ emitEvent: false });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  readonly kickbacks$ = combineLatest([this.service.organizationChanged, this.reloadKickbacks$]).pipe(
    filter(x => this.form().controls.payoutKickback.value === true),
    distinctUntilChanged(),
    switchMap(([orgId]) => {
      if (orgId) {
        return this.organizationService.getKickbacks(orgId).pipe(map(x => x.kickbacks));
      }
      return of([]);
    }),
    takeUntilDestroyed()
  );
}
